import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function capitalizeFirstLetter(value: string) {
  return `${value.charAt(0).toLocaleUpperCase()}${value.slice(1)}`;
}

// Fills `{key}` placeholders in a translation string. The i18n object holds
// plain strings (no ICU), so callers that need a count/name interpolate here.
export function fillTemplate(
  template: string,
  values: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

// The app is single-locale (default 'en', no currency threaded through the
// business/tier models), so USD is hard-coded here — first money formatter in
// the codebase. Introduced for quote amounts.
export function formatMoney(amount: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
}

// Copies `text` to the clipboard, resolving to whether it worked. Guards the
// non-secure-context / unsupported case where `navigator.clipboard` is missing.
export async function copyText(text: string): Promise<boolean> {
  try {
    if (!navigator.clipboard) return false;
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// "today" / "yesterday" / "3 days ago" / "in 5 days" from a date-ish string
// (bare YYYY-MM-DD or full ISO). Day-granularity only — used for series
// "last serviced" / "next visit" copy.
export function formatRelativeDays(value: string): string {
  const then = new Date(value.length <= 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(then.getTime())) return '';

  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  const diffDays = Math.round((startOfDay(then) - startOfDay(new Date())) / dayMs);

  if (diffDays === 0) return 'today';
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
  return rtf.format(diffDays, 'day');
}

export function getInitials(name: string | null) {
  if (!name) return '';

  const [first, last] = name.split(' ');

  return first && last
    ? `${first.charAt(0).toUpperCase()}${last.charAt(0).toUpperCase()}`
    : first.charAt(0).toUpperCase();
}
